/**
 * Dashboard side of the money contract 2 management projection (route families
 * F6 unit tariff and F7 pricing profiles): turns the exact stored tariff and the
 * server's currency choices into editor state, and describes a stored tariff.
 *
 * Financial facts come only from money_v2, never from a legacy pricebook,
 * a locale or a client currency list. A tariff that is invalid, unavailable or
 * holds a rate that cannot be read is never shown as free. New prices are in the
 * tenant's operating currency the server projects; the operator never chooses a
 * currency. A saved tariff keeps its own currency, and prices that the server's
 * verdicts do not allow for this tenant stay visible but cannot be saved.
 */
import {
  compareDecimal,
  formatRateV2,
  type CurrencyChoiceV2,
  type ManagementRead,
  type OperatingCurrencyV2,
  type PricingProfileManagementV2,
  type RateV2,
  type TariffManagementV2,
  type TariffMoneyV2,
  type TariffRuleV2,
} from "@voltie/localization-web";
import type { FeeType, Uom } from "@/lib/api";
import { localization, type Translate } from "@/app/localization";
import {
  SUMMARY_ORDER, SUMMARY_RATE, savedRateTooPrecise,
  type PricingFormValues, type PricingRuleValues,
} from "@/lib/pricingForm";

/** Why the editor shows no form: the saved data is unreadable, or it could not be loaded. */
export type UnavailableReason = "unreadable" | "unavailable";

/** The editable values of a read management body, or why there are none. */
export function managementValue<T>(read: ManagementRead<T> | undefined): { ok: true; value: T } | { ok: false; reason: UnavailableReason } | null {
  if (read === undefined) return null;
  return read.status === "value" ? { ok: true, value: read.value } : { ok: false, reason: "unavailable" };
}

const isExact = (rate: TariffRuleV2["rate"]): rate is RateV2 => !("diagnostic" in rate);

function ruleValues(rule: TariffRuleV2, currency: string): PricingRuleValues {
  const window = {
    fee_type: rule.fee_type as FeeType,
    uom: rule.uom as Uom,
    is_tou: rule.is_tou,
    start_time: rule.start_time,
    end_time: rule.end_time,
    days: rule.days,
    priority: rule.priority,
  };
  const rate = rule.rate;
  if (isExact(rate)) return { ...window, rate: rate.amount, saved: { kind: "exact", amount: rate.amount, currency } };
  return { ...window, rate: rate.raw, saved: { kind: "unreadable", raw: rate.raw } };
}

/**
 * Form values of a stored tariff: its own currency, its rules in stored order with
 * their windows and exact rates. An unset tariff starts empty with no currency (see
 * withTenantCurrency); an invalid or unavailable one cannot be edited.
 */
export function tariffValues(tariff: TariffMoneyV2): { ok: true; value: PricingFormValues } | { ok: false; reason: UnavailableReason } {
  switch (tariff.tariff_state) {
    case "configured":
      return { ok: true, value: { currency: tariff.currency, rules: tariff.rules.map((r) => ruleValues(r, tariff.currency)) } };
    case "unset":
      return { ok: true, value: { currency: "", rules: [] } };
    case "invalid":
      return { ok: false, reason: "unreadable" };
    default:
      return { ok: false, reason: "unavailable" };
  }
}

/*
 * A 2xx write answer confirms the write only when it projects what was written. Any
 * other answer (unreadable, unavailable, another record or currency) leaves the
 * outcome unknown: the write may have been applied, so it is neither reported as
 * saved nor as failed, and it is never sent again automatically.
 */
const writtenIn = (tariff: TariffMoneyV2, currency: string) =>
  tariff.tariff_state === "configured" && tariff.currency === currency;

export function tariffWriteConfirmed(read: ManagementRead<TariffManagementV2>, currency: string): boolean {
  return read.status === "value" && writtenIn(read.value.tariff, currency);
}

export function profileWriteConfirmed(
  read: ManagementRead<PricingProfileManagementV2>,
  expected: { orgId: number; profileId?: number; currency: string },
): boolean {
  if (read.status !== "value") return false;
  const { profile } = read.value;
  return profile.organization_id === expected.orgId && profile.enabled
    && (expected.profileId === undefined || profile.id === expected.profileId)
    && writtenIn(profile.tariff, expected.currency);
}

/** A disable answer keeps the pricebook only when no other write intervened, so its tariff is not required. */
export function profileDisableConfirmed(read: ManagementRead<PricingProfileManagementV2>, expected: { orgId: number; profileId: number }): boolean {
  if (read.status !== "value") return false;
  const { profile } = read.value;
  return profile.organization_id === expected.orgId && profile.id === expected.profileId && !profile.enabled;
}

/** The server's verdict allows new prices in this currency. */
export function canWriteCurrency(choices: readonly CurrencyChoiceV2[], currency: string): boolean {
  return choices.some((c) => c.currency === currency && c.new_write_verdict === "eligible");
}

/**
 * The tenant's operating currency as the editors use it. Known: the server projected a
 * currency (assigned, or USD for an unassigned tenant), writable only when both its own
 * verdict and its server choice allow new prices. Unavailable: the server said so, or the
 * member is missing, malformed or contradicted; never USD.
 */
export type TenantCurrency =
  | { readonly status: "known"; readonly currency: string; readonly writable: boolean }
  | { readonly status: "unavailable" };

export function tenantCurrency(operating: ManagementRead<OperatingCurrencyV2>, choices: readonly CurrencyChoiceV2[]): TenantCurrency {
  if (operating.status !== "value" || operating.value.currency === null) return { status: "unavailable" };
  const { currency, new_write_verdict } = operating.value;
  return { status: "known", currency, writable: new_write_verdict === "eligible" && canWriteCurrency(choices, currency) };
}

/**
 * A new tariff or profile takes the tenant's currency; values that already carry one
 * (a saved tariff, or a draft started under an earlier answer) keep it and are returned
 * unchanged, so a later change of the tenant can block them but never relabels them.
 */
export function withTenantCurrency(values: PricingFormValues, tenant: TenantCurrency): PricingFormValues {
  return values.currency === "" && tenant.status === "known" ? { ...values, currency: tenant.currency } : values;
}

/**
 * Whether prices in the form's currency may be saved for this tenant: writable, saved in
 * another currency than the tenant's, in the tenant's currency that the server does not
 * allow for new prices yet, or the tenant's currency is unavailable.
 */
export type CurrencyStatus = "writable" | "other_currency" | "not_ready" | "unavailable";

export function currencyStatus(values: PricingFormValues, tenant: TenantCurrency): CurrencyStatus {
  if (tenant.status === "unavailable" || values.currency === "") return "unavailable";
  if (values.currency !== tenant.currency) return "other_currency";
  return tenant.writable ? "writable" : "not_ready";
}

/** Something the server would refuse or the form cannot write: save stays disabled until it is changed. */
export function saveBlocked(values: PricingFormValues, tenant: TenantCurrency): boolean {
  return currencyStatus(values, tenant) !== "writable" || values.rules.some((r) => r.saved?.kind === "unreadable" || savedRateTooPrecise(r));
}

/** A saved rate with its currency code and every digit: "USD 0.0000001". */
export function savedRateText(rule: PricingRuleValues): string | null {
  if (rule.saved?.kind !== "exact") return null;
  const { amount, currency } = rule.saved;
  try {
    return formatRateV2({ currency, amount, unit: "kWh", precision_origin: "legacy_snapshot", money_version: 2 }, { minimumFractionDigits: 0 });
  } catch {
    return `${currency} ${amount}`;
  }
}

function configuredRates(tariff: TariffMoneyV2): { tariff: Extract<TariffMoneyV2, { tariff_state: "configured" }>; rules: (TariffRuleV2 & { rate: RateV2 })[] } | null {
  if (tariff.tariff_state !== "configured" || tariff.reason !== null || !tariff.rules.every((r) => isExact(r.rate))) return null;
  return { tariff, rules: tariff.rules as (TariffRuleV2 & { rate: RateV2 })[] };
}

/** Free or paid by the exact stored rates; null when the tariff or one of its rates cannot be read. */
export function tariffMode(tariff: TariffMoneyV2): "free" | "paid" | null {
  const known = configuredRates(tariff);
  if (known === null) return null;
  return known.rules.some((r) => compareDecimal(r.rate.amount, "0") > 0) ? "paid" : "free";
}

/**
 * The shared price summary of a stored tariff with exact rates and its currency code,
 * e.g. "USD 0.20/kWh + USD 1.00/hr idle", "Free", or "Prices unavailable" when the
 * tariff or a rate cannot be read. A representative rate per fee and unit: an all-day
 * rate wins, else the highest window rate.
 */
export function summarizeTariff(tariff: TariffMoneyV2, t: Translate = localization.t): string {
  const known = configuredRates(tariff);
  if (known === null) return t("billing.tariffs.summary_unavailable");
  const rep = new Map<string, { rate: RateV2; allDay: boolean }>();
  for (const r of known.rules) {
    const key = `${r.fee_type}|${r.uom}`;
    const cur = rep.get(key);
    const replace = cur === undefined || (r.is_tou ? !cur.allDay && compareDecimal(r.rate.amount, cur.rate.amount) > 0 : !cur.allDay);
    if (replace) rep.set(key, { rate: r.rate, allDay: !r.is_tou });
  }
  const keys = [...SUMMARY_ORDER.filter((k) => rep.has(k)), ...[...rep.keys()].filter((k) => !SUMMARY_ORDER.includes(k))];
  const parts: string[] = [];
  for (const key of keys) {
    const { rate } = rep.get(key)!;
    if (compareDecimal(rate.amount, "0") <= 0) continue;
    let amount: string;
    try {
      amount = formatRateV2(rate, { minimumFractionDigits: known.tariff.exponent });
    } catch {
      return t("billing.tariffs.summary_unavailable");
    }
    parts.push(t("billing.tariffs.summary_rate", { rate: SUMMARY_RATE[key] ?? "other", amount, uom: key.split("|")[1] }));
  }
  if (!parts.length) return t("billing.tariffs.free");
  // " + " joins rates like a formula; it is a symbol, not a word.
  const summary = parts.join(" + ");
  return known.rules.some((r) => r.is_tou) ? t("billing.tariffs.summary_time_based", { summary }) : summary;
}
